'use client';
import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LogoCarousel } from './atoms/LogoCarousel';
import { MarkDown } from './atoms/MarkDown';

const HERO_COMPONENT_NAMES = new Set([
    'Hero',
    'HeroFullBG',
    'HeroHalf',
    'AboutPageHero',
    'FaqHero',
    'ReviewPage',
    'WorkPage',
]);

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        [
            'features',
            'feature',
            'features-only',
            'feature-only',
            'badge',
            'badges',
            'badge-only',
            'badges-only',
            'logos',
            'logo',
        ].includes(v)
    )
        return 'features';
    if (
        ['cta', 'ctas', 'cta-only', 'ctas-only', 'buttons', 'button'].includes(
            v
        )
    )
        return 'ctas';
    return 'content';
}

function getGridColumnsClass(numBadges, mobileColumns) {
    if (numBadges === 1) return 'grid-cols-1';
    if (numBadges === 2) return 'grid-cols-2';
    if ([4, 7, 8].includes(numBadges)) return 'grid-cols-4';
    if ([3, 5, 6, 9].includes(numBadges))
        return `grid-cols-${mobileColumns} md:grid-cols-3`;
    return `grid-cols-${mobileColumns} md:grid-cols-3`;
}

function isRemoteUrl(url) {
    return url.startsWith('http');
}

function makeLocalUrlRefImages(url) {
    return url.startsWith('/images') ? url : '/images/' + url.split('/').pop();
}

function getScrollSpeed(raw) {
    const legacySpeed = String(raw || '').toLowerCase();
    if (legacySpeed === 'slow') return 3;
    if (legacySpeed === 'standard') return 5;
    if (legacySpeed === 'fast') return 8;
    const speed = Number(raw);
    if (!Number.isFinite(speed)) return 5;
    return Math.min(10, Math.max(3, speed));
}

function getRotationDelay(raw) {
    const delay = Number(raw);
    if (!Number.isFinite(delay)) return 2;
    return Math.min(5, Math.max(2, delay));
}

export default function Credentials(props) {
    const {
        Tagline,
        Header,
        Body,
        Badges = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'secondary',
        VerticalSpacing = 'medium',
        Layout = 'Default',
        RotationDelay = 2,
        ScrollSpeed = 5,
        LogoSize = 'Default',
        Animation = 'none',
        Duration = 0.6,
        AnimationGroup = 'features',
        EditorPreview = false,
        __isFirstContent = false,
        __nextComponentName,
    } = props;

    const {
        backgroundColor,
        textColor,
        headerColor,
        badgeColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const numBadges = Badges.length;
    const mobileColumns = 2;
    const normalizedLayout = String(Layout || 'Default')
        .toLowerCase()
        .trim();
    const isTwoColumnLayout = [
        'two-column',
        'two column',
        'twocolumn',
    ].includes(normalizedLayout);
    const isColumnsLayout = [
        'columns',
        'column',
        'rotating',
        'rotating logos',
        'rotating-logos',
    ].includes(normalizedLayout);
    const isMarqueeLayout = [
        'marquee',
        'scrolling',
        'scrolling logos',
        'scrolling-logos',
    ].includes(normalizedLayout);
    const isLogoCarouselLayout = isColumnsLayout || isMarqueeLayout;
    const normalizedLogoSize = String(LogoSize || 'Default')
        .toLowerCase()
        .trim();
    const isSmallLogo = normalizedLogoSize === 'small';
    const isExtraSmallLogo = [
        'really small',
        'really-small',
        'extra small',
        'extra-small',
    ].includes(normalizedLogoSize);
    const twoColumnLogoSizeClass = isExtraSmallLogo
        ? 'max-h-10 lg:max-h-20'
        : isSmallLogo
        ? 'max-h-14 lg:max-h-28'
        : numBadges === 1
        ? 'max-h-24 lg:max-h-52'
        : 'max-h-20 lg:max-h-40';
    const logoImageSizeClass = isExtraSmallLogo
        ? 'max-h-10 max-w-20 lg:max-h-14 lg:max-w-28'
        : isSmallLogo
        ? 'max-h-14 max-w-28 lg:max-h-20 lg:max-w-36'
        : 'max-h-20 max-w-36 lg:max-h-28 lg:max-w-48';
    const logoSlotSizeClass = isExtraSmallLogo
        ? 'h-10 min-w-16 max-w-20 lg:h-14 lg:min-w-20 lg:max-w-28'
        : isSmallLogo
        ? 'h-14 min-w-20 max-w-28 lg:h-20 lg:min-w-28 lg:max-w-36'
        : 'h-20 min-w-28 max-w-36 lg:h-28 lg:min-w-40 lg:max-w-48';
    const shouldLayerAboveFollowingHero =
        __isFirstContent && HERO_COMPONENT_NAMES.has(__nextComponentName);
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const [hoveredBadge, setHoveredBadge] = useState(null);
    const [navClearance, setNavClearance] = useState(0);
    const sectionRef = useRef(null);
    const hasBadges = numBadges > 0;
    const hasTextContent = Boolean(
        String(Tagline || '').trim() ||
            String(Header || '').trim() ||
            String(Body || '').trim()
    );
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    useLayoutEffect(() => {
        if (!__isFirstContent || !sectionRef.current) {
            setNavClearance(0);
            return undefined;
        }

        const section = sectionRef.current;
        const ownerDocument = section.ownerDocument;
        const ownerWindow = ownerDocument?.defaultView;
        const nav = ownerDocument?.querySelector('[data-component="Nav"]');
        if (!ownerWindow || !nav) return undefined;

        let frame = 0;
        const measure = () => {
            ownerWindow.cancelAnimationFrame(frame);
            frame = ownerWindow.requestAnimationFrame(() => {
                const scrollTop =
                    ownerWindow.scrollY ||
                    ownerDocument.documentElement?.scrollTop ||
                    0;
                if (scrollTop > 1) return;

                const sectionTop = section.getBoundingClientRect().top;
                const navBottom = nav.getBoundingClientRect().bottom;
                const nextClearance = Math.max(
                    0,
                    Math.ceil(navBottom - sectionTop)
                );
                setNavClearance((current) =>
                    current === nextClearance ? current : nextClearance
                );
            });
        };

        measure();
        const secondFrame = ownerWindow.requestAnimationFrame(measure);
        ownerWindow.addEventListener('resize', measure);

        const observer =
            typeof ownerWindow.ResizeObserver === 'function'
                ? new ownerWindow.ResizeObserver(measure)
                : null;
        observer?.observe(nav);

        return () => {
            ownerWindow.cancelAnimationFrame(frame);
            ownerWindow.cancelAnimationFrame(secondFrame);
            ownerWindow.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [__isFirstContent]);

    const preset = normalizeAnimation(Animation);
    const requestedGroup = mapGroup(AnimationGroup);
    const group =
        requestedGroup === 'features' && !hasBadges
            ? 'content'
            : requestedGroup === 'ctas' && !hasAnyCTA
            ? 'content'
            : requestedGroup;
    const m = useMotionControls({
        preset,
        duration: Number(Duration) || 0.6,
        editorPreview: EditorPreview,
        group,
        keyBase: 'credentials',
    });
    const badgeChildMotionProps = useMemo(() => {
        if (!m.enableAnim) return m.childMotionProps;
        if (preset !== 'stagger' || group !== 'features') {
            return m.childMotionProps;
        }
        return {
            variants: {
                hidden: { opacity: 0, y: 12 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: Math.max(0.3, Number(Duration) - 0.1 || 0.2),
                    },
                },
            },
        };
    }, [m.childMotionProps, m.enableAnim, preset, group, Duration]);

    const ctaAnimGroup = 'ctas';
    const buttonAlign = isTwoColumnLayout
        ? 'justify-center lg:justify-start'
        : 'justify-center';

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            enableAnim: m.enableChild(ctaAnimGroup),
            motionProps: m.childMotionProps,
        }
    );

    return (
        <section
            ref={sectionRef}
            className={`editable-component relative ${
                shouldLayerAboveFollowingHero ? 'z-10' : ''
            } ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={backgroundStyle}
            data-json="credentials"
        >
            {navClearance > 0 && (
                <div
                    aria-hidden="true"
                    data-credentials-nav-clearance
                    style={{ height: `${navClearance}px` }}
                />
            )}
            {isTwoColumnLayout ? (
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-x-8 gap-y-12 lg:grid-cols-2">
                    <div className="mx-auto w-full max-w-xl text-center lg:mx-0 lg:text-left">
                        <MotionMaybe
                            enable={m.enableContainer('content')}
                            nodeKey={`${m.remountKey}---text`}
                            motionProps={m.containerMotionProps}
                            className=""
                        >
                            {Tagline && (
                                <MotionMaybe
                                    enable={m.enableChild('content')}
                                    nodeKey={`${m.remountKey}---tagline`}
                                    motionProps={m.childMotionProps}
                                >
                                    <MarkDown
                                        className={`mb-3 max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${headerColor}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </MotionMaybe>
                            )}
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---header`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    variant="h2"
                                    className={`font-display text-3xl tracking-tight ${headerColor} lg:text-4xl`}
                                >
                                    {Header}
                                </MarkDown>
                            </MotionMaybe>
                            <MotionMaybe
                                enable={m.enableChild('content')}
                                nodeKey={`${m.remountKey}---body`}
                                motionProps={m.childMotionProps}
                            >
                                <MarkDown
                                    className={`mt-6 text-lg font-text leading-8 ${textColor}`}
                                >
                                    {Body}
                                </MarkDown>
                            </MotionMaybe>
                            {hasCTAs && (
                                <MotionMaybe
                                    enable={m.enableContainer(ctaAnimGroup)}
                                    nodeKey={`${m.remountKey}---ctas`}
                                    motionProps={m.containerMotionProps}
                                    className={`mt-8 flex flex-wrap items-center ${buttonAlign} gap-4`}
                                >
                                    <MotionMaybe
                                        enable={m.enableChild(ctaAnimGroup)}
                                        nodeKey={`${m.remountKey}---ctas-child`}
                                        motionProps={m.childMotionProps}
                                        className="flex flex-wrap items-center gap-4"
                                    >
                                        {CTAs}
                                    </MotionMaybe>
                                </MotionMaybe>
                            )}
                        </MotionMaybe>
                    </div>

                    <MotionMaybe
                        enable={m.enableContainer('features')}
                        nodeKey={`${m.remountKey}---badges`}
                        motionProps={m.containerMotionProps}
                        className={`mx-auto grid w-full max-w-xl ${getGridColumnsClass(
                            numBadges,
                            mobileColumns
                        )} items-center gap-12 sm:gap-y-14 lg:mx-0 lg:max-w-none lg:pl-8 ${badgeColor}`}
                    >
                        {Badges.map((Badge, index) => {
                            const badgeKey = `${m.remountKey}---badge-${index}`;
                            const imageElement = (
                                <img
                                    className={`${twoColumnLogoSizeClass} w-full object-contain object-center`}
                                    src={
                                        isRemoteUrl(Badge.Image)
                                            ? Badge.Image
                                            : makeLocalUrlRefImages(Badge.Image)
                                    }
                                    alt={Badge.AltText}
                                    width={128}
                                    height={64}
                                    loading="lazy"
                                    style={{
                                        filter:
                                            hoveredBadge === index && Badge.Link
                                                ? 'brightness(0.7)'
                                                : 'none',
                                        transition: 'filter 0.3s ease-in-out',
                                    }}
                                />
                            );

                            const badgeNode = Badge.Link ? (
                                <a
                                    href={Badge.Link}
                                    onMouseEnter={() => setHoveredBadge(index)}
                                    onMouseLeave={() => setHoveredBadge(null)}
                                    className="block w-full"
                                >
                                    {imageElement}
                                </a>
                            ) : (
                                imageElement
                            );

                            return (
                                <MotionMaybe
                                    key={badgeKey}
                                    nodeKey={badgeKey}
                                    enable={m.enableChild('features')}
                                    motionProps={badgeChildMotionProps}
                                    className="flex w-full justify-center"
                                >
                                    {badgeNode}
                                </MotionMaybe>
                            );
                        })}
                    </MotionMaybe>
                </div>
            ) : (
                <div className="mx-auto max-w-7xl">
                    {hasTextContent && (
                        <div className="mx-auto mb-6 w-full text-center md:mb-10 lg:mx-0">
                            <MotionMaybe
                                enable={m.enableContainer('content')}
                                nodeKey={`${m.remountKey}---text`}
                                motionProps={m.containerMotionProps}
                                className=""
                            >
                                {Tagline && (
                                    <MotionMaybe
                                        enable={m.enableChild('content')}
                                        nodeKey={`${m.remountKey}---tagline`}
                                        motionProps={m.childMotionProps}
                                    >
                                        <MarkDown
                                            className={`mx-auto mb-3 max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${headerColor}`}
                                        >
                                            {Tagline}
                                        </MarkDown>
                                    </MotionMaybe>
                                )}
                                <MotionMaybe
                                    enable={m.enableChild('content')}
                                    nodeKey={`${m.remountKey}---header`}
                                    motionProps={m.childMotionProps}
                                >
                                    <MarkDown
                                        variant="h2"
                                        className={`mx-auto max-w-2xl font-display text-3xl tracking-tight lg:max-w-4xl ${headerColor} lg:text-4xl`}
                                    >
                                        {Header}
                                    </MarkDown>
                                </MotionMaybe>
                                <MotionMaybe
                                    enable={m.enableChild('content')}
                                    nodeKey={`${m.remountKey}---body`}
                                    motionProps={m.childMotionProps}
                                >
                                    <MarkDown
                                        className={`mx-auto mt-6 max-w-2xl text-lg font-text leading-8 lg:max-w-4xl ${textColor}`}
                                    >
                                        {Body}
                                    </MarkDown>
                                </MotionMaybe>
                            </MotionMaybe>
                        </div>
                    )}

                    {isLogoCarouselLayout ? (
                        <MotionMaybe
                            enable={m.enableContainer('features')}
                            nodeKey={`${m.remountKey}---badges`}
                            motionProps={m.containerMotionProps}
                            className={`mx-auto w-full ${badgeColor}`}
                        >
                            <LogoCarousel
                                variant={
                                    isMarqueeLayout ? 'marquee' : 'columns'
                                }
                                columnCount={4}
                                rotationDelay={getRotationDelay(RotationDelay)}
                                scrollSpeed={getScrollSpeed(ScrollSpeed)}
                                className={
                                    isColumnsLayout ? 'gap-6 md:gap-10' : 'py-2'
                                }
                            >
                                {Badges.map((Badge, index) => {
                                    const badgeKey = `${m.remountKey}---badge-${index}`;
                                    const imageElement = (
                                        <img
                                            className={`object-contain object-center ${logoImageSizeClass}`}
                                            src={
                                                isRemoteUrl(Badge.Image)
                                                    ? Badge.Image
                                                    : makeLocalUrlRefImages(
                                                          Badge.Image
                                                      )
                                            }
                                            alt={Badge.AltText}
                                            width={192}
                                            height={112}
                                            loading="lazy"
                                            style={{
                                                filter:
                                                    hoveredBadge === index &&
                                                    Badge.Link
                                                        ? 'brightness(0.7)'
                                                        : 'none',
                                                transition:
                                                    'filter 0.3s ease-in-out',
                                            }}
                                        />
                                    );

                                    const badgeNode = Badge.Link ? (
                                        <a
                                            href={Badge.Link}
                                            onMouseEnter={() =>
                                                setHoveredBadge(index)
                                            }
                                            onMouseLeave={() =>
                                                setHoveredBadge(null)
                                            }
                                            className="inline-flex"
                                        >
                                            {imageElement}
                                        </a>
                                    ) : (
                                        imageElement
                                    );

                                    return (
                                        <MotionMaybe
                                            key={badgeKey}
                                            nodeKey={badgeKey}
                                            enable={m.enableChild('features')}
                                            motionProps={badgeChildMotionProps}
                                            className={`flex items-center justify-center px-2 ${logoSlotSizeClass}`}
                                        >
                                            {badgeNode}
                                        </MotionMaybe>
                                    );
                                })}
                            </LogoCarousel>
                        </MotionMaybe>
                    ) : (
                        <MotionMaybe
                            enable={m.enableContainer('features')}
                            nodeKey={`${m.remountKey}---badges`}
                            motionProps={m.containerMotionProps}
                            className={`mx-auto flex flex-wrap items-center justify-center gap-8 lg:gap-x-24 ${badgeColor}`}
                        >
                            {Badges.map((Badge, index) => {
                                const badgeKey = `${m.remountKey}---badge-${index}`;
                                const imageElement = (
                                    <img
                                        className={`object-contain lg:object-center ${logoImageSizeClass}`}
                                        src={
                                            isRemoteUrl(Badge.Image)
                                                ? Badge.Image
                                                : makeLocalUrlRefImages(
                                                      Badge.Image
                                                  )
                                        }
                                        alt={Badge.AltText}
                                        width={192}
                                        height={112}
                                        loading="lazy"
                                        style={{
                                            filter:
                                                hoveredBadge === index &&
                                                Badge.Link
                                                    ? 'brightness(0.7)'
                                                    : 'none',
                                            transition:
                                                'filter 0.3s ease-in-out',
                                        }}
                                    />
                                );

                                const badgeNode = Badge.Link ? (
                                    <a
                                        href={Badge.Link}
                                        onMouseEnter={() =>
                                            setHoveredBadge(index)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredBadge(null)
                                        }
                                        className="inline-flex"
                                    >
                                        {imageElement}
                                    </a>
                                ) : (
                                    imageElement
                                );

                                return (
                                    <MotionMaybe
                                        key={badgeKey}
                                        nodeKey={badgeKey}
                                        enable={m.enableChild('features')}
                                        motionProps={badgeChildMotionProps}
                                        className="inline-flex justify-center"
                                    >
                                        {badgeNode}
                                    </MotionMaybe>
                                );
                            })}
                        </MotionMaybe>
                    )}
                    {hasCTAs && CTAs}
                </div>
            )}
        </section>
    );
}
